/**
 * High-DPI raster tile sizing.
 *
 * Tiles were always requested at 256 px and painted into a 256 CSS-pixel
 * slot. On a HiDPI display (`devicePixelRatio` 1.5–3) the browser stretches
 * every tile pixel across 2+ device pixels, so a COG that looks sharp in
 * QGIS — which rasterises at the physical screen resolution — appears soft
 * in the viewer.
 *
 * The fix is the standard "@2x tile" trick: ask TiTiler for a larger PNG
 * (512 px) while still declaring a 256 px tile grid to the engine. Same
 * ground footprint, double the pixel density, no extra tile requests.
 */

/** Tile grid unit both MapLibre and OpenLayers are configured with. */
export const RASTER_TILE_GRID_SIZE = 256;

/** Cap the multiplier: 3× tiles cost ~9× decode for no visible gain. */
const MAX_PIXEL_RATIO = 2;

export function displayPixelRatio(): number {
  if (typeof window === 'undefined') return 1;
  const ratio = window.devicePixelRatio;
  if (!Number.isFinite(ratio) || ratio <= 0) return 1;
  return Math.min(MAX_PIXEL_RATIO, Math.max(1, Math.round(ratio)));
}

/**
 * Pixel size to request from TiTiler for one tile of the 256 px grid.
 * Returns 256 on standard displays and 512 on HiDPI ones.
 */
export function requestTileSize(): number {
  return RASTER_TILE_GRID_SIZE * displayPixelRatio();
}

/** Force `tilesize=` on a TiTiler template to the display-appropriate value. */
export function withDisplayTileSize(url: string, size = requestTileSize()): string {
  if (/[?&]tilesize=\d+/.test(url)) {
    return url.replace(/([?&])tilesize=\d+/g, `$1tilesize=${size}`);
  }
  return `${url}${url.includes('?') ? '&' : '?'}tilesize=${size}`;
}

/**
 * How many image pixels a template returns per grid pixel — OpenLayers and
 * Giro3D need this (`tilePixelRatio`) to lay a 512 px PNG onto the 256 px grid
 * instead of treating it as a tile covering 4× the ground.
 */
export function tilePixelRatioFor(url: string): number {
  const match = url.match(/[?&]tilesize=(\d+)/);
  if (!match) return 1;
  const size = Number(match[1]);
  if (!Number.isFinite(size) || size <= 0) return 1;
  return Math.max(1, Math.round(size / RASTER_TILE_GRID_SIZE));
}

/**
 * `raster-resampling` for a source whose native tiles stop at `nativeMaxZoom`.
 *
 * Below native resolution linear filtering avoids aliasing while panning.
 * Past it, linear turns real pixels into mush — QGIS shows crisp pixel edges
 * instead, which is what `nearest` reproduces.
 */
export function rasterResamplingExpression(
  nativeMaxZoom: number | undefined,
): 'linear' | ['step', ['zoom'], 'linear', number, 'nearest'] {
  if (typeof nativeMaxZoom !== 'number' || !Number.isFinite(nativeMaxZoom)) {
    return 'linear';
  }
  return ['step', ['zoom'], 'linear', nativeMaxZoom, 'nearest'];
}
